﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { BenchSheetListItem } from '../models/bench-sheet-list-item';
import { CoaComponent } from '../models/coa-component';
import { ProductBatchListItem } from '../models/product-batch-list-item';
import { ModalMetadata } from '../models/modal-metadata';
import { EditType, ModalMessageType } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import { ToastProps } from '../models/toast-props';

import DataGrid, {
    Column,
    Selection,
    Toolbar,
    Paging,
    Editing,
    Item as ToolBarItem
} from 'devextreme-react/data-grid';

import { Button } from 'devextreme-react/button';
import Guid from 'devextreme/core/guid';
import Toast from 'devextreme-react/toast';
import { ToastType } from 'devextreme/ui/toast';

export interface Props {
    coaId?: string;
    selectedBatch?: ProductBatchListItem;
    onClose?: () => void;
}

export default function CoaConfigurationBuilder(props: Props) {
    const [coaComponents, setCoaComponents] =
        useState<CoaComponent[]>([]);

    const [batchBenchSheets, setBatchBenchSheets] =
        useState<BenchSheetListItem[]>();

    const [selectedBenchSheet, setSelectedBenchSheet] =
        useState<BenchSheetListItem>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    return (
        <div className="pw-form-container">
            <div className="pw-comp-row pw-margin-top-05">
                <div className="lq-sub-title pw-margin-left-05 pw-margin-bottom-05">{props.selectedBatch ? props.selectedBatch.batchName : ''}</div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar pw-margin-right-05">
                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height='1.8rem'
                        onClick={onSaveClick} />

                    <Button id="closeButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height='1.8rem'
                        onClick={onCloseClick} />
                </div>
            </div>

            <div className="pw-comp-row">
                <DataGrid
                    height="16rem"
                    keyExpr="benchSheetId"
                    dataSource={coaComponents ? coaComponents : []}
                    onRowValidating={onCoaComponentRowValidating}
                    onRowRemoving={onCoaComponentRowDeleting}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}>

                    <Paging enabled={false} />
                    <Selection mode='single' />

                    <Column width='2rem' cssClass="table-vertical-align"
                        cellRender={editMarkerCellRender} />

                    <Column dataField='sequence' width='5rem' dataType="number" />
                    <Column dataField='technologyName' allowEditing={false} caption="Technology"
                        width='10rem' dataType="string" />
                    {/*
                    <Column dataField='benchSheetCreateTimestamp' allowEditing={false}
                        dataType="date" format="MM/dd/yyyy" caption="Date" width='10rem' />
                    <Column dataField='notes' width='20rem' dataType="string" />
                    */}

                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">QC Bench Sheets</div>
                            </div>
                        </ToolBarItem>

                    </Toolbar>

                    <Editing
                        mode="cell"
                        useIcons={true}
                        allowAdding={false}
                        allowUpdating={true}
                        allowDeleting={true} />

                </DataGrid>
            </div>

            <div className="pw-comp-row">
                <DataGrid
                    height="24rem"
                    keyExpr="benchSheetId"
                    onSelectionChanged={batchBenchSheetsSelectionChanged}
                    dataSource={batchBenchSheets}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}>

                    <Selection mode='single' />
                    <Paging enabled={false} />

                    {/*
                    <Column type="buttons" width='4rem' >
                        <ColumnButton hint="coa components" icon="edit"
                            onClick={addToCoaComponents} />
                    </Column>
                    */}


                    <Column dataField='technologyName' caption="Technology" width='10rem' dataType="string" />
                    <Column dataField='benchSheetCreateTimestamp' dataType="date" format="MM/dd/yyyy" caption="Date" width='10rem' />
                    
                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Batch QC Bench Sheets</div>
                            </div>
                        </ToolBarItem>


                        <ToolBarItem location='after'>
                            <Button type='normal' width='2.1rem' icon='inserttable'
                                text=''
                                hint='add to coa components'
                                onClick={addToCoaComponents} />
                        </ToolBarItem>

                    </Toolbar>
                </DataGrid>
            </div>

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onCloseClick() {
        if (props.onClose)
            props.onClose();
    }

    async function onSaveClick() {
        if (coaComponents) {
            await updateCoaComponents(coaComponents)
                .then(() => {
                    showToast("COA Components updated", "success");
                    getCoaComponents();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating COA Components");
                });
        }
    }


    /*
    function benchSheetsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) 
            setSelectedBenchSheets(e.selectedRowsData[0]);
    }
    */

    function batchBenchSheetsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedBenchSheet(e.selectedRowsData[0]);
    }

    function onCoaComponentRowValidating(e: any) {
        console.log("onCoaComponentRowValidating");
        console.log(e);

        const coaComponentId = e.oldData.coaComponentId;

        let editedComponents = [...coaComponents];

        let matchingComponent = editedComponents.find(c => c.coaComponentId === coaComponentId);

        if (matchingComponent) {
            if (matchingComponent.editType !== EditType.Insert)
                matchingComponent.editType = EditType.Update;
                
            setCoaComponents(editedComponents);
        }
    }

    async function onCoaComponentRowDeleting(e: any) {
        console.log("onCoaComponentRowDeleting");
        console.log(e);

        if (e.data && e.data.coaComponentId) {
            await removeCoaComponent(e.data.coaComponentId)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting COA Component");
                });
        }
    }

    function addToCoaComponents() {
        console.log("addToCoaComponents");
        console.log(selectedBenchSheet);

        let seq = 1;
        if (coaComponents)
            seq = coaComponents.length + 1;

        if (selectedBenchSheet) {
            const addedCoaComponent: CoaComponent = {
                coaComponentId: new Guid().toString(),
                coaId: props.coaId,
                benchSheetId: selectedBenchSheet.benchSheetId,
                technologyName: selectedBenchSheet.technologyName,
                technologyId: selectedBenchSheet.benchSheetTechnology,
                sequence: seq,
                editType: EditType.Insert
            };

            let editedCoaComponents = [...coaComponents];

            editedCoaComponents.push(addedCoaComponent);

            setCoaComponents(editedCoaComponents);
        }
    }

    async function getCoaComponents() {
        if (props.coaId)
            await CrudFunctions.getData(`coa/coacomponentsbycoaid/${props.coaId}`)
                .then((data) => {
                    setCoaComponents(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving COA Components");
                });
    }

    async function getAllBatchBenchSheets() {
        if (props.selectedBatch && props.selectedBatch.batchId)
            await CrudFunctions.getData(`benchsheets/benchsheetsandtechnologiesbybatchid/${props.selectedBatch.batchId}`)
                .then((data) => {
                    setBatchBenchSheets(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving QC Bench Sheets");
                });
    }

    // PUT functions

    async function updateCoaComponents(coaComponents: CoaComponent[]) {
        return await CrudFunctions.putData("coa/coacomponents", coaComponents);
    }

    async function removeCoaComponent(coaComponentId: string) {
        return await CrudFunctions.deleteData(`coa/coacomponent/${coaComponentId}`);
    }

    //todo - move to a common location
    function editMarkerCellRender(e: any) {
        if (e.data) {
            switch (e.data.editType) {
                case EditType.Insert:
                    return <div><i className="dx-icon-add" /></div>;
                case EditType.Update:
                    return <div><i className="dx-icon-edit" /></div>;
                case EditType.Delete:
                    return <div><i className="dx-icon-trash" /></div>;
                default:
                    return <div></div>;
            }
        }
        return <div></div>;
    }

    async function getAllData() {
        await Promise.all([
            getCoaComponents(),
            getAllBatchBenchSheets()
        ]);
    }

    //show/hide

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}


